import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Play, ExternalLink, ChevronLeft, Flame, Sun, Moon } from 'lucide-react';
import { useGameDetail, useTrackView, useAddHistory, useTrackClick, useTheme } from '@/hooks';
import { useDeviceType } from '@/utils/device';
import { buildImageUrl } from '@/utils/image-url';
import { LoadingSpinner, ErrorState, MobileFooter } from '@/components/common';
import { Sidebar, DesktopHeader } from '@/components/desktop';
import { useSiteSettings } from '@/contexts/settings-context';
import styles from './game-detail.module.css';

export const GameDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'mobile';
  const { theme, toggleTheme } = useTheme();
  const { features } = useSiteSettings();
  
  const gameId = id || '';
  
  const { 
    data: game, 
    isLoading, 
    error,
    refetch 
  } = useGameDetail(gameId);
  
  const trackView = useTrackView();
  const addHistory = useAddHistory();
  const trackClick = useTrackClick();

  useEffect(() => {
    if (game) {
      trackView.mutate(parseInt(game.id, 10) || 0);
      addHistory.mutate({
        id: game.id,
        name: game.name,
        title: game.title,
        icon: game.images.icon.primary,
        viewCount: game.viewCount,
      });
    }
  }, [game, gameId]);

  if (isLoading) {
    return <LoadingSpinner size="lg" text="加载游戏详情..." />;
  }

  if (error || !game) {
    return (
      <ErrorState
        title="加载失败"
        message="无法加载游戏详情"
        onRetry={() => refetch()}
      />
    );
  }

  const activePlatforms = game.platforms.filter(
    p => p.status === 'active' && p.url && p.url !== 'url' && p.url !== '暂无' && p.url !== 'wu'
  );

  const handlePlay = (platformId: string, url: string) => {
    trackClick.mutate({ gameId: parseInt(game.id, 10) || 0, platformId });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Mobile Layout - 参考原 mobile.html 布局
  if (isMobile) {
    return (
      <div className={styles.mobilePage}>
        {/* 顶部背景大图区域 */}
        <div className={styles.heroSection}>
          <div className={styles.heroMask} style={{ backgroundImage: `url(${buildImageUrl(game.images.cover.primary)})` }} />
          <div className={styles.heroCurvedBg} />
          
          {/* 返回按钮 */}
          <Link to="/" className={styles.heroBack}>
            <ChevronLeft size={24} />
          </Link>

          {/* 主题切换按钮 */}
          {features.darkMode && (
            <button className={styles.heroThemeToggle} onClick={toggleTheme} title={theme === 'dark' ? '切换亮色模式' : '切换暗色模式'}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}

          {/* 游戏信息浮层 */}
          <div className={styles.heroInfo}>
            <div className={styles.heroIconWrapper}>
              <img
                src={buildImageUrl(game.images.icon.primary)}
                alt={game.title}
                className={styles.heroIcon}
              />
            </div>
            <h1 className={styles.heroTitle}>{game.title}</h1>
            
            {/* 平台按钮 */}
            <div className={styles.heroButtons}>
              {activePlatforms.map(platform => (
                <button
                  key={platform.id}
                  className={styles.heroPlayBtn}
                  onClick={() => handlePlay(platform.id, platform.url)}
                >
                  <Play size={12} />
                  <span>{platform.name}</span>
                </button>
              ))}
              {activePlatforms.length === 0 && (
                <div className={styles.noPlatformHint}>暂无可用的云游戏平台</div>
              )}
            </div>
          </div>
        </div>

        {/* 截图滚动区域 */}
        {game.images.screenshots && game.images.screenshots.length > 0 && (
          <div className={styles.screenshotsScroll}>
            {game.images.screenshots.map((ss, index) => (
              <img
                key={index}
                src={buildImageUrl(ss.primary)}
                alt={`截图 ${index + 1}`}
                className={styles.screenshotItem}
                loading="lazy"
              />
            ))}
          </div>
        )}

        {/* 简介1 */}
        {game.content.intro1 && (
          <div className={styles.introSection}>
            <div className={styles.introIcon}>
              <Flame size={12} />
            </div>
            <p className={styles.introText}>{game.content.intro1}</p>
          </div>
        )}

        {/* 分隔线 */}
        <hr className={styles.divider} />

        {/* 游戏简介详情 */}
        {game.content.intro2 && (
          <div className={styles.detailSection}>
            <h3 className={styles.detailTitle}>游戏简介</h3>
            <p className={styles.detailText}>{game.content.intro2}</p>
          </div>
        )}
        <MobileFooter />
      </div>
    );
  }

  // Desktop Layout - 与首页一致的全局风格
  return (
    <div className={styles.desktopPage}>
      <Sidebar activePath="/" />

      <div className={styles.desktopContent}>
        <DesktopHeader showSearch={true} />

        <main className={styles.desktopMain}>
          {/* 游戏信息头部 */}
          <div className={styles.gameHeader}>
            <img
              src={buildImageUrl(game.images.icon.primary)}
              alt={game.title}
              className={styles.gameIcon}
            />
            <div className={styles.gameInfo}>
              <h1 className={styles.gameTitle}>{game.title}</h1>
              <div className={styles.gameButtons}>
                {activePlatforms.map(platform => (
                  <button
                    key={platform.id}
                    className={styles.desktopPlayBtn}
                    onClick={() => handlePlay(platform.id, platform.url)}
                  >
                    <Play size={14} />
                    <span>{platform.name}</span>
                    <ExternalLink size={12} />
                  </button>
                ))}
                {activePlatforms.length === 0 && (
                  <div className={styles.noPlatformHint}>暂无可用的云游戏平台</div>
                )}
              </div>
            </div>
          </div>

          <hr className={styles.divider} />

          {/* 截图 */}
          {game.images.screenshots && game.images.screenshots.length > 0 && (
            <div className={styles.screenshots}>
              {game.images.screenshots.map((ss, index) => (
                <img
                  key={index}
                  src={buildImageUrl(ss.primary)}
                  alt={`截图 ${index + 1}`}
                  className={styles.screenshot}
                  loading="lazy"
                />
              ))}
            </div>
          )}

          {/* 简介1 */}
          {game.content.intro1 && (
            <p className={styles.intro1}>{game.content.intro1}</p>
          )}

          {/* 详细简介 */}
          {game.content.intro2 && (
            <div className={styles.detailSection}>
              <h3 className={styles.detailTitle}>游戏简介</h3>
              <p className={styles.detailText}>{game.content.intro2}</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default GameDetailPage;
